"use client";

import React from "react";
import Image from "next/image";
import SafeHtml from "@/components/ui/SafeHtml";
import { Button } from "@/components/ui/Button";
import { ArrowIcon } from "@/components/ui/Icons"; 
import { toAbsoluteUrl } from "@/lib/toAbsoluteUrl";
import type { ContentFields } from "@/types/acf";

type Align = "left" | "center" | "right";

export default function Content({ data }: { data: ContentFields }) {
  if (!data || data.hide) return null;

  const title = data.title ?? "";
  const text = data.text ?? "";

  const titleImage =
    data.image_title && typeof data.image_title === "object" ? data.image_title : null;

  const titleImageSrc = titleImage?.url ? toAbsoluteUrl(titleImage.url) : null;

  if (!title && !text && !data.button && !titleImageSrc) return null;

  const align: Align = (data.text_align as Align) ?? "left";

  const alignWrapClass =
    align === "center"
      ? "text-center items-center"
      : align === "right"
        ? "text-right items-end"
        : "text-left items-start";

  const justifyClass =
    align === "center" ? "justify-center" : align === "right" ? "justify-end" : "justify-start";

  const rawHref =
    data.button && typeof data.button === "object" ? (data.button.url ?? "") : "";

  const isAnchorLink = rawHref.trim().startsWith("#"); //

  return (
    <div className={`min-w-0 flex flex-col ${alignWrapClass}`} id={data.id}>
      {titleImageSrc ? (
        <div className={`max-w-full w-full flex ${justifyClass}`}>
          <Image
            src={titleImageSrc}
            alt={titleImage?.alt || ""}
            width={titleImage?.width || 1}
            height={titleImage?.height || 1}
            className="h-auto max-w-full"
            sizes="100vw"
            priority={false}
          />
        </div>
      ) : null}

      {title ? (
        <h2 className="text-title-2 !m-0">
          <SafeHtml content={title} />
        </h2>
      ) : null}

      {text ? (
        <div
          className="
            mt-4 min-w-0 break-words
            [&_img]:max-w-full [&_img]:h-auto
            [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto
          "
        >
          <SafeHtml content={text} flow />
        </div>
      ) : null}

      {data.button && typeof data.button === "object" && data.button.url ? (
        <div className={`mt-6 w-full flex ${justifyClass}`}>
          <Button
            as="link"
            href={toAbsoluteUrl(data.button.url) ?? "#"}
            target={data.button.target || "_self"}
            rel={data.button.target === "_blank" ? "noopener noreferrer" : undefined}
            icon={isAnchorLink ? <ArrowIcon direction="down" /> : undefined} 
          >
            {data.button.title || "Learn more"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
